import { Box, IconButton, InputAdornment } from "@mui/material";
import { Col, Form, Row } from "react-bootstrap";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Clear } from "@mui/icons-material";

import { QUIZ_TYPES_CONTANTS } from "app/types/quiz-constants";
import BackgroundImageInput from "./BackgroundImageInput";
import InstructionInput from "./InstructionInput";
import { SelectInputType } from "mocks/quizInputs";
import {
  CognitiveCategoryType,
  CollectionType,
  QuestionType,
  QuizType,
} from "app/types";
import { QuestionFormType } from "../update-type";
import { useEffect, useState } from "react";
import AudioInput from "./AudioInput";
import { Box as BoxComponent } from "app/pages/admin/components";

type ContentQuestionFormViewProps = {
  cognitiveCategories: Array<CognitiveCategoryType>;
  audiometryCategories: CognitiveCategoryType[];
  audioDeviceType: any[];
  audioChannel: any[];
  styleInput: {
    backgroundColor: string;
    color: string;
  };
  control: Control<QuestionFormType, any>;
  quiz: QuizType;
  selectedType: SelectInputType;
  preViewImage: string;
  errors: FieldErrors<QuestionType>;
  collections: Array<CollectionType>;
  cognitiveInputs: Array<{ type: string; name: string }>;
  watch: UseFormWatch<QuestionFormType>;
  setValue: UseFormSetValue<QuestionFormType>;
  translate: (key: string) => string;
  register: UseFormRegister<QuestionFormType>;
  handleFileQuestion: (event: React.ChangeEvent<HTMLInputElement>) => void;
  helperText: (text?: string) => JSX.Element;
  handleClearQuest: () => void;
  handleQuestChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const ContentQuestionFormView = ({
  cognitiveCategories,
  audiometryCategories,
  audioDeviceType,
  audioChannel,
  styleInput,
  control,
  quiz,
  selectedType,
  preViewImage,
  errors,
  collections,
  cognitiveInputs,
  translate,
  register,
  handleFileQuestion,
  helperText,
  watch,
  handleClearQuest,
  handleQuestChange,
  setValue,
}: ContentQuestionFormViewProps) => {
  const [questionsByPhase, setQuestionsByPhase] = useState<QuestionType[]>([]);

  const [selected, setSelected] = useState("opcao1");

  const handleChange = (e: any) => setSelected(e.target.value);

  useEffect(() => {
    setQuestionsByPhase(
      quiz.questions.filter(
        (question: QuestionType) =>
          question.categoryCognitiveQuestionId ===
          Number(watch("categoryCognitiveQuestionId")),
      ),
    );
  }, [watch("categoryCognitiveQuestionId")]);

  return (
    <Row>
      <Form.Group as={Col} lg="12" xs="12" className="mb-0 pt-2">
        <Form.Label>{translate("screens.quiz.labels.question")} *</Form.Label>
        <Box display="flex" alignItems="center">
          <Form.Control
            placeholder={translate("screens.quiz.labels.question")}
            {...register("quest")}
            onChange={handleQuestChange}
            as="textarea"
            rows={1}
            style={styleInput}
          />
          <InputAdornment position="end">
            <IconButton
              title={translate("buttons_clean")}
              onClick={handleClearQuest}
            >
              <Clear />
            </IconButton>
          </InputAdornment>
        </Box>
        {errors.quest && helperText(errors.quest.message)}
      </Form.Group>
      <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
        <Form.Label>{translate("screenApp.quiz.questionType")} *</Form.Label>
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Form.Select
              {...field}
              style={styleInput}
              value={
                QUIZ_TYPES_CONTANTS.AUDIOMETRY === +quiz.type ? "checkbox" : ""
              }
              disabled={QUIZ_TYPES_CONTANTS.AUDIOMETRY === +quiz.type}
            >
              <option value="">
                {translate("screenApp_quiz_selectOption")}
              </option>
              {cognitiveInputs.map((value, index) => (
                <option value={value.type} key={index}>
                  {value.name}
                </option>
              ))}
            </Form.Select>
          )}
        />
        {errors.type && helperText(errors.type.message)}
      </Form.Group>
      <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
        <Form.Label>{translate("screens.quiz.labels.sequence")} *</Form.Label>
        <Form.Control
          type="number"
          min="1"
          {...register("arrangeQuestionSequence")}
          style={styleInput}
        />
        {errors.arrangeQuestionSequence &&
          helperText(errors.arrangeQuestionSequence.message)}
      </Form.Group>
      {quiz.type !== "7" &&
        selectedType !== "radio" &&
        +quiz.type !== QUIZ_TYPES_CONTANTS.AUDIOMETRY && (
          <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
            <Form.Label>
              {translate("screens.fragrance.questionReferenceFragrance")}
            </Form.Label>
            <Controller
              control={control}
              name="fragranceId"
              render={({ field }) => (
                <Form.Select {...field} style={styleInput}>
                  <option value="">
                    {translate("screens.quiz.placeholders.fragrances")}
                  </option>
                  {collections
                    .sort((a, b) => a.slot - b.slot)
                    .map(collection => (
                      <option
                        key={collection.fragrance.fragranceId}
                        value={collection.fragrance.fragranceId}
                      >
                        {`${collection.slot} - ${collection.fragrance.name}`}
                      </option>
                    ))}
                </Form.Select>
              )}
            />
            {errors.fragranceId && helperText(errors.fragranceId.message)}
          </Form.Group>
        )}
      <InstructionInput
        register={register("instruction")}
        styleInput={styleInput}
        selectedType={selectedType}
      />

      {+quiz.type === QUIZ_TYPES_CONTANTS.AUDIOMETRY ? (
        <>
          <AudioInput
            label={`Selecione o áudio`}
            onChange={handleFileQuestion}
            previewAudio={preViewImage}
          />
        </>
      ) : (
        <>
          <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
            <Form.Label>
              {translate("screens_fragrance_labels_description")}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              {...register("description")}
              style={styleInput}
            />
          </Form.Group>
          <BackgroundImageInput
            previewImage={preViewImage}
            label={
              selectedType === "speechtotext" &&
              [
                QUIZ_TYPES_CONTANTS.CHILDHOOD_IDENTIFICATION.toString(),
                QUIZ_TYPES_CONTANTS.COGNITIVE.toString(),
              ].includes(quiz.type)
                ? "Imagem da pergunta"
                : "Imagem de fundo da pergunta"
            }
            style={styleInput}
            onChange={handleFileQuestion}
          />
        </>
      )}

      {QUIZ_TYPES_CONTANTS.COGNITIVE === +quiz.type && (
        <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
          <Form.Label>Tempo (em segundos)</Form.Label>
          <Form.Control
            as="input"
            type="number"
            step={30}
            defaultValue={0}
            min={0}
            {...register("timeInSeconds")}
            style={styleInput}
          />
        </Form.Group>
      )}
      {QUIZ_TYPES_CONTANTS.COGNITIVE === +quiz.type &&
        cognitiveCategories.length && (
          <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
            <Form.Label>
              {translate("screens_collectionDevice_table_cognitive")}
            </Form.Label>
            <Controller
              name="categoryCognitiveQuestionId"
              control={control}
              render={({ field }) => (
                <Form.Select {...field} style={styleInput}>
                  <option value="">
                    {translate("screenApp_quiz_selectOption")}
                  </option>
                  {cognitiveCategories.map(value => (
                    <option
                      value={value.categoryCognitiveQuestionId}
                      key={value.name}
                    >
                      {value.name}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
          </Form.Group>
        )}
      {QUIZ_TYPES_CONTANTS.AUDIOMETRY === +quiz.type &&
        audiometryCategories.length && (
          <>
            <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
              <Form.Label>
                {translate("screens_collectionDevice_table_audiometry")}
              </Form.Label>
              <Controller
                name="categoryCognitiveQuestionId"
                control={control}
                render={({ field }) => (
                  <Form.Select {...field} style={styleInput}>
                    <option value="">
                      {translate("screenApp_quiz_selectOption")}
                    </option>
                    {audiometryCategories.map(value => (
                      <option
                        value={value.categoryCognitiveQuestionId}
                        key={value.name}
                      >
                        {value.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              {errors.categoryCognitiveQuestionId &&
                helperText(errors.categoryCognitiveQuestionId.message)}
            </Form.Group>
            {/* {questionsByPhase.length > 0 && (
              <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
                <Form.Label>
                  {translate("screens_audiometry_next_question")}
                </Form.Label>
                <Controller
                  name="instruction"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field} style={styleInput}>
                      <option value="">
                        {translate("screenApp_quiz_selectOption")}
                      </option>
                      {questionsByPhase.map(value => (
                        <option value={value.questionId} key={value.questionId}>
                          {value.question ? value.question.quest : value.quest}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                {errors.instruction && helperText(errors.instruction.message)}
              </Form.Group>
            )} */}
            <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
              <Form.Label>
                {translate("screens_table_audiometry_audio_device_type")}
              </Form.Label>
              <Controller
                name="instruction"
                control={control}
                render={({ field }) => (
                  <Form.Select {...field} style={styleInput}>
                    <option value="">
                      {translate("screenApp_quiz_selectOption")}
                    </option>
                    {audioDeviceType.map(value => (
                      <option value={value.value} key={value.name}>
                        {value.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              {errors.categoryCognitiveQuestionId &&
                helperText(errors.categoryCognitiveQuestionId.message)}
            </Form.Group>

            <Form.Group as={Col} lg="6" xs="12" className="mb-0 pt-2">
              <Form.Label>
                {translate("screens_table_audiometry_audio_channel")}
              </Form.Label>
              <BoxComponent
                style={{
                  display: "flex",
                  padding: "0px",
                  borderRadius: 0,
                  justifyContent: "start",
                  gap: "5px",
                }}
              >
                {audioChannel &&
                  audioChannel.length > 0 &&
                  audioChannel.map((audioElement, index) => (
                    <Form.Check
                      key={index}
                      name="fragranceId"
                      type="radio"
                      label={audioElement.name}
                      value={audioElement.value}
                      checked={watch("fragranceId") === audioElement.value}
                      // onChange={handleChange}
                      onChange={(e: any) => {
                        setValue("fragranceId", e.target.value);
                      }}
                    />
                  ))}
              </BoxComponent>
              {errors.fragranceId && helperText(errors.fragranceId.message)}
            </Form.Group>
          </>
        )}
    </Row>
  );
};

export default ContentQuestionFormView;
